import { useCallback } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import { RPCMethod } from '~/app/connect/types'
import { QUERY_KEYS } from '~/constants/react-query'
import { useActiveOrgId } from '~/hooks/org'
import { useServerRequests } from '~/hooks/useRequests'
import { useTransactions } from '~/hooks/useTransactions'
import { BatchOperationInput, MetadataPayloads, OrgTransaction } from '~/types'
import { getPathWithParams } from '~/utils/requests'
import { isDraftBatchTransaction } from '~/utils/smartAccounts/batch'

// Kill-switches for batching external dApp requests. Flip to `false` to
// disable batching for that method without touching call sites.
//
// `eth_sendTransaction`: when batched, we do NOT respond to the dApp — there
// is no real tx hash to return, and a fabricated one can't be reconciled.
// SplitsConnect ends in a JSON-RPC reject; WalletConnect leaves the request
// orphaned. The "Added to batch" screen warns users about this.
//
// `wallet_sendCalls` (EIP-5792): when batched, we respond with an `id`
// derived from the operationId via `operationIdToHex`. The backend reverses
// this to serve `wallet_getCallsStatus`, so dApps can poll batch state.
const METHOD_BATCHING_ENABLED: Partial<Record<string, boolean>> = {
  [RPCMethod.EthSendTransaction]: true,
  [RPCMethod.WalletSendCalls]: true,
}

const useBatchCacheSync = () => {
  const queryClient = useQueryClient()
  const activeOrgId = useActiveOrgId()

  return useCallback(
    async (transaction?: OrgTransaction) => {
      if (transaction && activeOrgId) {
        queryClient.setQueryData(
          [QUERY_KEYS.TRANSACTION, activeOrgId, transaction.id],
          transaction,
        )
      }
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.TRANSACTIONS],
      })
    },
    [activeOrgId, queryClient],
  )
}

// Backend enforces one active draft batch per org, so we can safely return
// the first match. Returns undefined when no draft batch exists yet.
const useActiveDraftBatch = (): OrgTransaction | undefined => {
  const { transactions } = useTransactions({ hideDust: true })
  return transactions.find(
    (t): t is OrgTransaction =>
      t.isSplitsTransaction && isDraftBatchTransaction(t),
  )
}

export type AddToBatchStatus =
  { canAdd: true } | { canAdd: false; disabledReason: string }

const CAN_ADD: AddToBatchStatus = { canAdd: true }
const CANNOT_ADD_ACCOUNT: AddToBatchStatus = {
  canAdd: false,
  disabledReason: 'A batch is in progress on another account',
}
const CANNOT_ADD_CHAIN: AddToBatchStatus = {
  canAdd: false,
  disabledReason: 'A batch is in progress on another chain',
}
const CANNOT_ADD_EXTERNAL_DIRECT: AddToBatchStatus = {
  canAdd: false,
  disabledReason: 'This type of external app request cannot be batched',
}

// Account mismatch wins over chain mismatch since switching accounts is the
// bigger move.
export const useCanAddToBatch = ({
  smartAccountId,
  chainId,
  requestMethod,
}: {
  smartAccountId?: string
  chainId?: number
  requestMethod?: string
}): AddToBatchStatus => {
  const activeBatch = useActiveDraftBatch()
  if (requestMethod && METHOD_BATCHING_ENABLED[requestMethod] === false)
    return CANNOT_ADD_EXTERNAL_DIRECT
  if (!activeBatch || !smartAccountId || !chainId) return CAN_ADD
  if (activeBatch.smartAccountId !== smartAccountId) return CANNOT_ADD_ACCOUNT
  if (activeBatch.chainId !== chainId) return CANNOT_ADD_CHAIN
  return CAN_ADD
}

export const useAddToTransactionBatch = () => {
  const { serverPostRequest, isProcessing } = useServerRequests()
  const syncCache = useBatchCacheSync()

  const addToBatch = useCallback(
    async (params: {
      smartAccountId: string
      chainId: number
      operation: BatchOperationInput
      metadata: MetadataPayloads[]
      memo?: string
    }) => {
      const result = await serverPostRequest<{ batch: OrgTransaction }>({
        path: '/api/smart_accounts/transactions/batch',
        params,
        errorMessage: 'Failed to add transaction to batch',
      })

      const batch = result?.data.batch
      if (!batch) return undefined

      await syncCache(batch)
      return batch
    },
    [serverPostRequest, syncCache],
  )

  return { addToBatch, isProcessing }
}

export const useRemoveTransactionBatchOperation = () => {
  const { serverDeleteRequest, isProcessing } = useServerRequests()
  const syncCache = useBatchCacheSync()

  const removeBatchOperation = useCallback(
    async ({
      smartAccountId,
      operationId,
    }: {
      smartAccountId: string
      operationId: string
    }) => {
      const result = await serverDeleteRequest<{
        batch: OrgTransaction | null
      }>({
        path: getPathWithParams(
          `/api/smart_accounts/transactions/batch/operation/${operationId}`,
          { smartAccountId },
        ),
        errorMessage: 'Failed to remove transaction from batch',
      })

      const batch = result?.data.batch ?? null
      await syncCache(batch ?? undefined)
      return batch
    },
    [serverDeleteRequest, syncCache],
  )

  return { removeBatchOperation, isProcessing }
}

export const useSubmitTransactionBatch = () => {
  const { serverPostRequest, isProcessing } = useServerRequests()
  const syncCache = useBatchCacheSync()

  const submitBatch = useCallback(
    async ({ smartAccountId }: { smartAccountId: string }) => {
      const result = await serverPostRequest<{ transaction: OrgTransaction }>({
        path: '/api/smart_accounts/transactions/batch/submit',
        params: { smartAccountId },
        errorMessage: 'Failed to submit batch',
      })

      const transaction = result?.data.transaction
      if (!transaction) return undefined

      await syncCache(transaction)
      return transaction
    },
    [serverPostRequest, syncCache],
  )

  return { submitBatch, isProcessing }
}

export const useDeleteTransactionBatch = () => {
  const { serverDeleteRequest, isProcessing } = useServerRequests()
  const queryClient = useQueryClient()
  const activeOrgId = useActiveOrgId()
  const syncCache = useBatchCacheSync()

  const deleteBatch = useCallback(
    async ({
      smartAccountId,
      transactionId,
    }: {
      smartAccountId: string
      transactionId: string
    }) => {
      const result = await serverDeleteRequest({
        path: getPathWithParams('/api/smart_accounts/transactions/batch', {
          smartAccountId,
        }),
        errorMessage: 'Failed to cancel batch',
      })

      if (!result) return false

      if (activeOrgId) {
        queryClient.removeQueries({
          queryKey: [QUERY_KEYS.TRANSACTION, activeOrgId, transactionId],
        })
      }
      await syncCache()
      return true
    },
    [activeOrgId, queryClient, serverDeleteRequest, syncCache],
  )

  return { deleteBatch, isProcessing }
}
